import React, { useState, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Calendar,
  Clock,
  MapPin,
  BookOpen,
  Users,
  ChevronLeft,
  ChevronRight,
  Filter,
} from "lucide-react";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { AuthContext } from "../../contexts/AuthContext";
import { getClasses } from "../../services/classes";
import { getTeachers } from "../../services/teachers";
import { ClassWithDetails } from "../../types/class";

type DayOfWeek =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

interface SchedulePeriod {
  day: DayOfWeek;
  startTime: string;
  endTime: string;
  subjectName: string;
  teacherId: string;
  room?: string;
}

const DAYS_OF_WEEK: DayOfWeek[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];
const DAY_NAMES: Record<DayOfWeek, string> = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

const TIME_SLOTS = [
  "08:00",
  "08:30",
  "09:00",
  "09:30",
  "10:00",
  "10:30",
  "11:00",
  "11:30",
  "12:00",
  "12:30",
  "13:00",
  "13:30",
  "14:00",
  "14:30",
  "15:00",
  "15:30",
  "16:00",
  "16:30",
  "17:00",
  "17:30",
  "18:00",
];

// Helper function to check if a time slot is within a period's duration
const isTimeSlotInPeriod = (
  timeSlot: string,
  period: SchedulePeriod
): boolean => {
  const slotTime = timeSlot.replace(":", "");
  const startTime = period.startTime.replace(":", "");
  const endTime = period.endTime.replace(":", "");

  const slot = parseInt(slotTime);
  const start = parseInt(startTime);
  const end = parseInt(endTime);

  return slot >= start && slot < end;
};

// Helper function to calculate period span (how many slots it covers)
const calculatePeriodSpan = (period: SchedulePeriod): number => {
  const startTime = period.startTime.replace(":", "");
  const endTime = period.endTime.replace(":", "");

  const start = parseInt(startTime);
  const end = parseInt(endTime);

  // Calculate duration in minutes
  const startMinutes = Math.floor(start / 100) * 60 + (start % 100);
  const endMinutes = Math.floor(end / 100) * 60 + (end % 100);
  const durationMinutes = endMinutes - startMinutes;

  // Each slot is 30 minutes, so divide by 30
  return Math.ceil(durationMinutes / 30);
};

const TeacherSchedule: React.FC = () => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const [selectedDay, setSelectedDay] = useState<DayOfWeek | "all">("all");
  const [currentWeek, setCurrentWeek] = useState(0);

  // Fetch all classes and filter for teacher's classes
  const {
    data: classesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["teacher-classes"],
    queryFn: () => getClasses(),
    enabled: !!user?.id,
  });

  // Fetch teachers to match teacher._id with teacher.userId
  const { data: teachersData, isLoading: teachersLoading } = useQuery({
    queryKey: ["teachers"],
    queryFn: () => getTeachers({ page: 1, limit: 100 }),
    enabled: !!user?.id,
  });

  if (isLoading || teachersLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t-2 border-emerald-500"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading schedule...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6 flex items-center justify-center">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 max-w-md">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-300 to-pink-400 flex items-center justify-center mx-auto mb-4">
            <Calendar className="h-6 w-6 text-white" />
          </div>
          <h3 className="text-slate-900 font-bold text-center text-lg">
            Error Loading Schedule
          </h3>
          <p className="text-slate-600 font-medium mt-2 text-center">
            Unable to load your schedule. Please try refreshing the page.
          </p>
        </div>
      </div>
    );
  }

  // Filter classes where teacher is assigned
  const allClasses = (classesData as any)?.classes || [];
  const teachers = teachersData?.data || [];

  const teacherClasses = allClasses.filter((classItem: any) => {
    if (!classItem.schedule?.periods) {
      return false;
    }
    const hasTeacherPeriods = classItem.schedule.periods.some((period: any) => {
      // The period.teacherId is the Teacher document _id, but we need to compare with the User id
      // We need to find teachers and match teacherId with teacher._id, then compare teacher.userId with user.id
      const matchingTeacher = teachers.find(
        (teacher: any) =>
          String(teacher._id) === String(period.teacherId) &&
          String(teacher.userId) === String(user?.id)
      );
      return !!matchingTeacher;
    });
    return hasTeacherPeriods;
  });

  // Get all periods for the teacher
  const teacherPeriods: (SchedulePeriod & {
    className: string;
    courseName: string;
  })[] = [];
  teacherClasses.forEach((classItem: any) => {
    if (classItem.schedule?.periods) {
      classItem.schedule.periods.forEach((period: any) => {
        // The period.teacherId is the Teacher document _id, but we need to compare with the User id
        // We need to find teachers and match teacherId with teacher._id, then compare teacher.userId with user.id
        const matchingTeacher = teachers.find(
          (teacher: any) =>
            String(teacher._id) === String(period.teacherId) &&
            String(teacher.userId) === String(user?.id)
        );
        if (matchingTeacher) {
          teacherPeriods.push({
            day: period.day,
            startTime: period.startTime,
            endTime: period.endTime,
            subjectName: period.subjectName,
            teacherId: period.teacherId,
            room: period.room,
            className: classItem.name,
            courseName: "General Course", // We can enhance this later
          });
        }
      });
    }
  });

  // Filter periods by selected day
  const filteredPeriods =
    selectedDay === "all"
      ? teacherPeriods
      : teacherPeriods.filter((period) => period.day === selectedDay);

  // Group periods by day for calendar view
  const periodsByDay = DAYS_OF_WEEK.reduce((acc, day) => {
    acc[day] = teacherPeriods.filter((period) => period.day === day);
    return acc;
  }, {} as Record<DayOfWeek, typeof teacherPeriods>);

  // Get subject color
  const getSubjectColor = (subject: string) => {
    const colors = [
      "bg-gradient-to-br from-blue-300 to-indigo-400 text-white",
      "bg-gradient-to-br from-emerald-300 to-emerald-400 text-white",
      "bg-gradient-to-br from-purple-300 to-purple-400 text-white",
      "bg-gradient-to-br from-amber-300 to-yellow-400 text-white",
      "bg-gradient-to-br from-red-300 to-pink-400 text-white",
      "bg-gradient-to-br from-blue-300 to-purple-400 text-white",
    ];
    const index = subject.length % colors.length;
    return colors[index];
  };

  // Get current week dates
  const getCurrentWeekDates = () => {
    const today = new Date();
    const currentDay = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - currentDay + 1 + currentWeek * 7);

    return DAYS_OF_WEEK.map((_, index) => {
      const date = new Date(monday);
      date.setDate(monday.getDate() + index);
      return date;
    });
  };

  const weekDates = getCurrentWeekDates();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-3 sm:p-4 lg:p-6">
      {/* Header */}
      <div className="mb-6 lg:mb-8 bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
          My Schedule
        </h1>
        <p className="text-slate-600 mt-2 text-sm sm:text-base font-medium">
          View your teaching schedule and assignments
        </p>
      </div>

      {/* Week Navigation */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg p-3 sm:p-4 mb-4 lg:mb-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setCurrentWeek(currentWeek - 1)}
            className="flex items-center space-x-2 px-2 sm:px-3 py-2 text-slate-700 hover:text-slate-900 bg-white border border-slate-200 hover:shadow-lg rounded-xl transition-all duration-300 hover:scale-105 text-sm font-bold"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Previous Week</span>
            <span className="sm:hidden">Prev</span>
          </button>

          <div className="text-center">
            <h3 className="text-sm sm:text-lg font-bold text-slate-900">
              {weekDates[0].toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              })}{" "}
              -{" "}
              {weekDates[6].toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </h3>
          </div>

          <button
            onClick={() => setCurrentWeek(currentWeek + 1)}
            className="flex items-center space-x-2 px-2 sm:px-3 py-2 text-slate-700 hover:text-slate-900 bg-white border border-slate-200 hover:shadow-lg rounded-xl transition-all duration-300 hover:scale-105 text-sm font-bold"
          >
            <span className="hidden sm:inline">Next Week</span>
            <span className="sm:hidden">Next</span>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg p-3 sm:p-4 mb-4 lg:mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-300 to-indigo-400 flex items-center justify-center shadow-sm">
              <Filter className="h-4 w-4 text-white" />
            </div>
            <span className="text-sm font-bold text-slate-900">
              Filter by day:
            </span>
          </div>
          <select
            value={selectedDay}
            onChange={(e) =>
              setSelectedDay(e.target.value as DayOfWeek | "all")
            }
            className="px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm font-medium bg-white shadow-sm"
          >
            <option value="all">All Days</option>
            {DAYS_OF_WEEK.map((day) => (
              <option key={day} value={day}>
                {DAY_NAMES[day]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Weekly Calendar View - Horizontally Scrollable */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg mb-4 lg:mb-6">
        <div className="p-3 sm:p-4 border-b border-slate-200/60">
          <h3 className="text-lg font-bold text-slate-900">Weekly Schedule</h3>
          <p className="text-sm text-slate-600 font-medium mt-1">
            Scroll horizontally to view all days
          </p>
        </div>

        <div className="overflow-x-auto">
          <div className="p-3 sm:p-4">
            <div className="grid grid-cols-8 gap-1 min-w-[800px]">
              {/* Ensures minimum width for better readability */}
              {/* Header row */}
              <div className="p-2 text-center font-bold text-slate-900 bg-gradient-to-r from-slate-50 to-blue-50 text-sm rounded-lg">
                Time
              </div>
              {DAYS_OF_WEEK.map((day, index) => (
                <div
                  key={day}
                  className="p-2 text-center font-bold text-slate-900 bg-gradient-to-r from-slate-50 to-blue-50 rounded-lg"
                >
                  <div className="text-sm">{DAY_NAMES[day]}</div>
                  <div className="text-xs text-slate-600 font-semibold">
                    {weekDates[index].toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                </div>
              ))}

              {/* Time slots */}
              {TIME_SLOTS.map((timeSlot) => (
                <React.Fragment key={timeSlot}>
                  <div className="p-2 text-center text-sm font-bold text-slate-700 bg-gradient-to-r from-slate-50 to-blue-50 border-r border-b border-slate-200/60 rounded-lg">
                    {timeSlot}
                  </div>
                  {DAYS_OF_WEEK.map((day) => {
                    const dayPeriods = periodsByDay[day] || [];

                    // Find period that starts at this time slot
                    const startingPeriod = dayPeriods.find(
                      (p) => p.startTime === timeSlot
                    );

                    // Find period that spans this time slot (but doesn't start here)
                    const spanningPeriod = dayPeriods.find(
                      (p) =>
                        isTimeSlotInPeriod(timeSlot, p) &&
                        p.startTime !== timeSlot
                    );

                    return (
                      <div
                        key={`${day}-${timeSlot}`}
                        className="p-1 border-r border-b border-slate-200/60 min-h-[60px] relative hover:bg-slate-50 transition-colors"
                      >
                        {startingPeriod && (
                          <div
                            className={`p-2 rounded-xl text-xs shadow-md ${getSubjectColor(
                              startingPeriod.subjectName
                            )}`}
                            style={{
                              height: `${
                                calculatePeriodSpan(startingPeriod) * 60 - 8
                              }px`,
                              zIndex: 10,
                              position: "absolute",
                              top: "4px",
                              left: "4px",
                              right: "4px",
                            }}
                          >
                            <div className="font-bold truncate">
                              {startingPeriod.subjectName}
                            </div>
                            <div className="text-xs opacity-90 font-medium">
                              {startingPeriod.className}
                            </div>
                            <div className="text-xs opacity-90 font-medium">
                              {startingPeriod.startTime} -{" "}
                              {startingPeriod.endTime}
                            </div>
                            {startingPeriod.room && (
                              <div className="text-xs opacity-90 font-medium">
                                Room: {startingPeriod.room}
                              </div>
                            )}
                          </div>
                        )}
                        {spanningPeriod && !startingPeriod && (
                          <div
                            className={`p-1 rounded-lg text-xs opacity-50 ${getSubjectColor(
                              spanningPeriod.subjectName
                            )}`}
                            style={{
                              position: "absolute",
                              top: "4px",
                              left: "4px",
                              right: "4px",
                              bottom: "4px",
                              zIndex: 5,
                            }}
                          >
                            {/* This shows the period continues from previous slot */}
                            <div className="text-center text-xs opacity-75 font-bold">
                              ↑ {spanningPeriod.subjectName}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Periods List - Mobile Friendly */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg mt-4 lg:mt-6">
        <div className="p-3 sm:p-4 border-b border-slate-200/60">
          <h3 className="text-lg font-bold text-slate-900">
            {selectedDay === "all"
              ? "All Classes"
              : `${DAY_NAMES[selectedDay as DayOfWeek]} Classes`}
          </h3>
        </div>

        <div className="p-3 sm:p-4">
          {filteredPeriods.length > 0 ? (
            <div className="space-y-3">
              {filteredPeriods.map((period, index) => (
                <div
                  key={index}
                  className="border border-slate-200/60 rounded-xl p-3 sm:p-4 bg-white hover:shadow-lg hover:scale-105 transition-all duration-300"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3 space-y-2 sm:space-y-0">
                    <div
                      className={`px-3 py-1.5 rounded-xl text-sm font-bold self-start shadow-sm ${getSubjectColor(
                        period.subjectName
                      )}`}
                    >
                      {DAY_NAMES[period.day]}
                    </div>
                    <div className="text-left sm:text-right">
                      <div className="text-sm font-bold text-slate-900">
                        {period.startTime} - {period.endTime}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-300 to-indigo-400 flex items-center justify-center shadow-sm">
                        <BookOpen className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-sm font-bold text-slate-900">
                        {period.subjectName}
                      </span>
                    </div>

                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-300 to-emerald-400 flex items-center justify-center shadow-sm">
                        <Users className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-sm text-slate-600 font-medium">
                        {period.className} | {period.courseName}
                      </span>
                    </div>

                    {period.room && (
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-300 to-purple-400 flex items-center justify-center shadow-sm">
                          <MapPin className="h-4 w-4 text-white" />
                        </div>
                        <span className="text-sm text-slate-600 font-medium">
                          Room: {period.room}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-300 to-indigo-400 flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Calendar className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                No classes scheduled
              </h3>
              <p className="text-slate-600 font-medium">
                {selectedDay === "all"
                  ? "You don't have any classes assigned yet."
                  : `No classes scheduled for ${
                      DAY_NAMES[selectedDay as DayOfWeek]
                    }.`}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeacherSchedule;
